import { type MaybeRefOrGetter, ref, toValue, watch } from 'vue';
import QRCode, { type QRCodeRenderersOptions } from 'qrcode';

export interface UseShareQrcodeOptions extends QRCodeRenderersOptions {
  /** 初始化时是否立即生成，默认 true */
  immediate?: boolean;
}

/**
 * 根据分享链接生成二维码（Data URL），链接变化时自动重新生成
 * @param source - 分享链接，支持 ref / getter / 普通字符串
 * @param options - 二维码生成选项（透传给 qrcode.toDataURL）
 * @returns { dataUrl, loading, error, generate }
 */
export const useShareQrcode = (
  source: MaybeRefOrGetter<string | null | undefined>,
  options: UseShareQrcodeOptions = {}
) => {
  const { immediate = true, ...qrOptions } = options;

  const dataUrl = ref<string>('');
  const loading = ref<boolean>(false);
  const error = ref<Error | null>(null);

  // 每次生成自增，回调返回时对比，丢弃过期请求，避免竞态覆盖最新结果
  let token = 0;

  const generate = async () => {
    const text = toValue(source);
    const current = ++token;
    error.value = null;

    if (!text) {
      dataUrl.value = '';
      loading.value = false;
      return;
    }

    loading.value = true;

    try {
      const url = await QRCode.toDataURL(text, {
        margin: 1,
        width: 300,
        errorCorrectionLevel: 'M',
        ...qrOptions
      });

      if (current !== token) return;
      dataUrl.value = url;
    } catch (e) {
      if (current !== token) return;
      error.value = e as Error;
      dataUrl.value = '';
    } finally {
      if (current === token) loading.value = false;
    }
  };

  watch(() => toValue(source), generate, { immediate });

  return { dataUrl, loading, error, generate };
};
